/**
 * Per-character classification: $P point-cloud distances against the built-in
 * templates and the player's calibration samples, adjusted by a few cheap
 * structural features where $P alone is weak:
 *
 * - "." — a dot normalises to the same shape as a "0", so dots are decided
 *   by size relative to the line height (or an absolute size for a lone mark).
 * - "-" — a straight, flat stroke; decided by straightness + angle + height.
 * - straight single strokes can only be "1", "/" or "-"; the lean angle
 *   separates "1" from "/".
 *
 * Scores are 0–1 (higher is better) and are *not* a probability distribution.
 */
import type { HwCandidate, HwChar } from '../../contracts.ts';
import { type Box, type Polyline, boundsOf, dist, pathLength, ramp, resampleStrokes } from './geometry.ts';
import { type Cloud, WEIGHT_SUM, cloudMatch, toCloud } from './pdollar.ts';

export const CLASSIFY = {
  /** Weighted mean point distance (unit box) at which $P similarity drops to 1/e. */
  SIM_SCALE: 0.1,
  /** User calibration samples are trusted more: their distances are multiplied by this. */
  USER_WEIGHT: 0.8,
  /** Distance penalty per stroke of difference from the template (capped at 2 strokes). */
  STROKE_PENALTY: 0.04,
  /** Dot: larger side ≤ FULL × H is certainly a dot, ≥ NONE × H certainly isn't. */
  DOT_FULL: 0.16,
  DOT_NONE: 0.3,
  /** Same, in px, when there is no reference height (a lone mark, e.g. the Watch pad). */
  DOT_FULL_PX: 6,
  DOT_NONE_PX: 14,
  /** Straightness (chord / path length) ramp for "this is a straight line". */
  STRAIGHT_FROM: 0.85,
  STRAIGHT_TO: 0.95,
  /** Dash: angle from horizontal ramp (degrees) and max height relative to H. */
  DASH_ANGLE_FROM: 30,
  DASH_ANGLE_TO: 15,
  DASH_HEIGHT_FROM: 0.5,
  DASH_HEIGHT_TO: 0.3,
  /** A dash must be at least this long (× H); shorter flicks are dots. */
  DASH_MIN_FROM: 0.12,
  DASH_MIN_TO: 0.2,
  /** Lean from vertical (degrees): "1" below ONE_TO, "/" above SLASH_TO. */
  ONE_FROM: 20,
  ONE_TO: 11,
  SLASH_FROM: 11,
  SLASH_TO: 20,
  /** 5 vs 3: how much a missing / present upper-left stem counts against a "5" / a "3". */
  STEM_PENALTY_5: 0.4,
  STEM_PENALTY_3: 0.5,
  /** How strongly a straight line suppresses characters that can't be straight. */
  STRAIGHT_PENALTY: 0.75,
  /** A rule-decided "." or "-" scores RULE_BASE + RULE_GAIN × certainty. */
  RULE_BASE: 0.55,
  RULE_GAIN: 0.4,
  /** …and damps every $P score by up to this share. */
  RULE_DAMP: 0.85,
  /** Confidence: full when the best score beats the second by this share of itself. */
  MARGIN_FULL: 0.35,
  /** Confidence floor factor for a best score with no margin at all. */
  MARGIN_FLOOR: 0.35,
  /** Candidates returned per character. */
  TOP_K: 3,
} as const;

export interface MatchTemplate {
  char: HwChar;
  cloud: Cloud;
  strokeCount: number;
  /** True for a player's calibration sample. */
  user: boolean;
}

export interface ClassifyContext {
  /** Reference character height of the row; null when unknown (single mark). */
  lineHeight: number | null;
}

/** Characters decided by rules, never by $P (a dot scaled up looks exactly like a 0). */
const RULE_ONLY: ReadonlySet<HwChar> = new Set<HwChar>(['.']);
/** Characters a single straight stroke can be. */
const LINE_CHARS: ReadonlySet<HwChar> = new Set<HwChar>(['1', '/', '-', '.']);

export function makeTemplate(char: HwChar, strokes: readonly Polyline[], user: boolean): MatchTemplate | null {
  const cloud = toCloud(strokes);
  return cloud ? { char, cloud, strokeCount: strokes.filter((s) => s.length).length, user } : null;
}

/** Map a $P distance to a 0–1 similarity. */
export const similarity = (distance: number): number => Math.exp(-((distance / WEIGHT_SUM / CLASSIFY.SIM_SCALE) ** 2));

interface Shape {
  box: Box;
  strokeCount: number;
  /** 0–1: how much the group looks like one straight line. */
  line: number;
  /** Chord angle from horizontal (0–90°) for single strokes. */
  angle: number;
  /** Signed lean from vertical in degrees; positive = top leans right, like "/". */
  lean: number;
  /** 0–1: share of the upper body (12–40% of the height) with ink near the left edge — the stem of a 5. */
  leftStem: number;
}

const STEM_BANDS = 4;
const STEM_TOP = 0.12;
const STEM_BOTTOM = 0.4;
const STEM_LEFT = 0.3;

/**
 * The upper-left stem is what separates a 5 from a 3 (their bellies are the
 * same): a 5 has ink running down the left side under its bar, a 3 doesn't.
 */
function leftStemOf(strokes: readonly Polyline[], box: Box): number {
  if (box.w <= 0 || box.h <= 0) return 0;
  const hit = new Array<boolean>(STEM_BANDS).fill(false);
  for (const p of resampleStrokes(strokes, 96).flat()) {
    const u = (p.x - box.x) / box.w;
    const v = (p.y - box.y) / box.h;
    if (u >= STEM_LEFT || v < STEM_TOP || v >= STEM_BOTTOM) continue;
    hit[Math.floor(((v - STEM_TOP) / (STEM_BOTTOM - STEM_TOP)) * STEM_BANDS)] = true;
  }
  return hit.filter(Boolean).length / STEM_BANDS;
}

function shapeOf(strokes: readonly Polyline[]): Shape {
  const box = boundsOf(strokes);
  const live = strokes.filter((s) => s.length);
  const leftStem = leftStemOf(live, box);
  if (live.length !== 1) return { box, strokeCount: live.length, line: 0, angle: 0, lean: 0, leftStem };
  const s = live[0]!;
  const a = s[0]!;
  const b = s[s.length - 1]!;
  const len = pathLength(s);
  const straightness = len > 0 ? dist(a, b) / len : 1;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const angle = len > 0 ? (Math.atan2(Math.abs(dy), Math.abs(dx)) * 180) / Math.PI : 0;
  // Lean: orient the chord top → bottom, then measure how far the top sits to the right.
  const [top, bottom] = a.y <= b.y ? [a, b] : [b, a];
  const lean = (Math.atan2(top.x - bottom.x, bottom.y - top.y) * 180) / Math.PI;
  const line = ramp(straightness, CLASSIFY.STRAIGHT_FROM, CLASSIFY.STRAIGHT_TO);
  return { box, strokeCount: 1, line, angle, lean, leftStem };
}

/** Certainty (0–1) that a group is a decimal point / a minus sign. */
function ruleScores(shape: Shape, ctx: ClassifyContext): { dot: number; dash: number } {
  const size = Math.max(shape.box.w, shape.box.h);
  const H = ctx.lineHeight;
  const tiny = H ? ramp(size / H, CLASSIFY.DOT_NONE, CLASSIFY.DOT_FULL) : ramp(size, CLASSIFY.DOT_NONE_PX, CLASSIFY.DOT_FULL_PX);
  // Too small to judge direction reliably → not a dash.
  const bigEnough = H ? ramp(size / H, CLASSIFY.DASH_MIN_FROM, CLASSIFY.DASH_MIN_TO) : ramp(size, 8, 14);
  const flat = ramp(shape.angle, CLASSIFY.DASH_ANGLE_FROM, CLASSIFY.DASH_ANGLE_TO);
  const low = H ? ramp(shape.box.h / H, CLASSIFY.DASH_HEIGHT_FROM, CLASSIFY.DASH_HEIGHT_TO) : 1;
  const dash = shape.line * flat * low * bigEnough;
  return { dot: tiny * (1 - dash), dash };
}

/**
 * Structural multiplier for a $P score. `personal` = the best match was one
 * of the player's calibration samples: the built-in 5-vs-3 stem heuristic is
 * then skipped, because the player has shown how *they* write it (a
 * mirror-written 3 has its spine on the left, like a 5).
 */
function prior(char: HwChar, shape: Shape, personal: boolean): number {
  const { line } = shape;
  const notLine = 1 - CLASSIFY.STRAIGHT_PENALTY * line;
  if (char === '5' && !personal) return notLine * (1 - CLASSIFY.STEM_PENALTY_5 * ramp(shape.leftStem, 0.5, 0));
  if (char === '3' && !personal) return notLine * (1 - CLASSIFY.STEM_PENALTY_3 * ramp(shape.leftStem, 0.5, 1));
  if (!LINE_CHARS.has(char)) return notLine;
  const tilt = Math.abs(90 - shape.angle); // degrees from vertical
  if (char === '1') return 1 - CLASSIFY.STRAIGHT_PENALTY * line * (1 - ramp(tilt, CLASSIFY.ONE_FROM, CLASSIFY.ONE_TO));
  if (char === '/') {
    const leansRight = shape.lean > 0 ? 1 : 0.3;
    return 1 - CLASSIFY.STRAIGHT_PENALTY * line * (1 - ramp(tilt, CLASSIFY.SLASH_FROM, CLASSIFY.SLASH_TO) * leansRight);
  }
  return 1;
}

/**
 * Score every character for one group of strokes and return the best
 * candidates (highest first).
 */
export function classifyGroup(
  strokes: readonly Polyline[],
  ctx: ClassifyContext,
  templates: readonly MatchTemplate[],
): HwCandidate[] {
  const cloud = toCloud(strokes);
  const shape = shapeOf(strokes);
  /** Best (weighted) $P distance per character, and whether it came from a calibration sample. */
  const best = new Map<HwChar, { d: number; user: boolean }>();
  if (cloud) {
    for (const t of templates) {
      if (RULE_ONLY.has(t.char)) continue;
      const bound = best.get(t.char)?.d ?? Infinity;
      const weight = (t.user ? CLASSIFY.USER_WEIGHT : 1) * (1 + CLASSIFY.STROKE_PENALTY * Math.min(2, Math.abs(t.strokeCount - shape.strokeCount)));
      const d = cloudMatch(cloud, t.cloud, bound / weight) * weight;
      if (d < bound) best.set(t.char, { d, user: t.user });
    }
  }

  const { dot, dash } = ruleScores(shape, ctx);
  // A rule-decided mark keeps a little of the $P ordering so the correction chips still have sensible alternatives.
  const ruleDamp = 1 - CLASSIFY.RULE_DAMP * Math.max(dot, dash);
  const scores = new Map<HwChar, number>();
  for (const [char, m] of best) scores.set(char, similarity(m.d) * prior(char, shape, m.user) * ruleDamp);
  if (dot > 0) scores.set('.', Math.max(scores.get('.') ?? 0, CLASSIFY.RULE_BASE + CLASSIFY.RULE_GAIN * dot));
  if (dash > 0) scores.set('-', Math.max(scores.get('-') ?? 0, CLASSIFY.RULE_BASE + CLASSIFY.RULE_GAIN * dash));

  return [...scores]
    .map(([char, score]) => ({ char, score: Math.min(1, Math.max(0, score)) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, CLASSIFY.TOP_K);
}

/**
 * Confidence of one character's reading: its best score, discounted when the
 * runner-up is close (an ambiguous 1/7 or 4/9 shouldn't be trusted).
 */
export function charConfidence(candidates: readonly HwCandidate[]): number {
  const s1 = candidates[0]?.score ?? 0;
  if (s1 <= 0) return 0;
  const s2 = candidates[1]?.score ?? 0;
  const margin = ramp((s1 - s2) / s1, 0, CLASSIFY.MARGIN_FULL);
  return s1 * (CLASSIFY.MARGIN_FLOOR + (1 - CLASSIFY.MARGIN_FLOOR) * margin);
}
